/**
 * Iter-57 — Focused Funnel landing-page experience.
 *
 * A distraction-free mobile-first qualification flow: icon-based accident
 * selection, one question per screen, large touch targets. Runs alongside
 * (never replacing) Standard and Quiz.
 *
 * Submits to the SAME `/api/public/landing/lead` endpoint every other
 * experience uses. `form_experience=focused_funnel`, `fault_level`,
 * `incident_timeframe`, and `focused_funnel_session_id` are pushed onto
 * `lead.extra` untouched — existing `at_fault` / `incident_date`
 * semantics are NEVER fabricated from a fault bucket / timeframe range.
 *
 * Preview / demo / sample surfaces short-circuit BEFORE any real network
 * write — no TrustedForm, no session telemetry, no lead insert, no
 * conversion pixels.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import {
  Car, Truck, Bike, Users, PersonStanding, HelpCircle, ArrowLeft,
  CircleUserRound,
} from "lucide-react";
import api from "@/lib/api";
import { routedThankYou } from "@/lib/routedBuyer";
import { formBadgeHidden } from "@/lib/previewBadge";
import { TCPA_LANGUAGE, siteText } from "./config";
import { isValidEmail, isValidZip } from "./publicFormValidators";
import { formatUSPhone, isValidUSPhone } from "./publicFormPhone";
import {
  AFPI_CHOICES,
  requiresAtFaultPartyInsured,
} from "./mvaApplicability";
import { emitPreviewEngagement } from "@/lib/prospectEngagement";
import { fieldRulePage, useFieldRules, zipOk } from "@/lib/useFieldRules";

const FF_RULE_KEY = {
  injury: "physically_injured", treatment: "medical_treatment", attorney: "currently_represented",
  fault: "at_fault", timeframe: "incident_age_bucket", at_fault_party_insured: "at_fault_party_insured",
};
import { ConsentText, readTrustedFormCert, TrustedFormOffer, trustedFormOn } from "./trustedForm";
import {
  ACCIDENT_TYPE_OPTIONS, FAULT_OPTIONS, TIMEFRAME_OPTIONS,
  DISQUALIFICATION_COPY, resolveFocusedFunnelConfig,
} from "./focusedFunnelConfig";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
  ["DC","District of Columbia"],
];

const ICON_MAP = {
  Car, Truck, Bike, Users, PersonStanding, HelpCircle, BikeIcon: Bike,
};

// Iter-63.1.12A verification -- Focused Funnel raw accident_type
// (consumer-facing selection) → canonical backend accident_type sent
// as `case_type` on the wire. The frontend `car` label maps to the
// canonical `auto`. Every other selection now flows through UNCHANGED
// so pedestrian and bicycle no longer accidentally collapse to `other`
// on either side of the wire. Backend
// `routes.public_routes._CASE_TYPE_TO_ACCIDENT_TYPE` MUST accept the
// same keys so the resolved `accident_type` matches the consumer's
// actual selection regardless of which landing-page slug hosts the
// funnel.
const FF_ACCIDENT_TYPE_TO_CANONICAL = {
  car:        "auto",
  truck:      "truck",
  motorcycle: "motorcycle",
  rideshare:  "rideshare",
  pedestrian: "pedestrian",
  bicycle:    "bicycle",
  other:      "other",
};

// Iter-63.1.12A verification -- canonical `fault_level` → `at_fault`
// mapping. This is a TRUTHFUL projection: any explicit fault-level
// answer produces a defensible boolean. `not_sure` is deliberately
// absent from the map so we NEVER fabricate a boolean for an
// ambiguous consumer answer; the lookup returns undefined and the
// submit payload sends `at_fault: null` (leaving the lead INCOMPLETE
// per the canonical completeness engine — no consumer sees a false
// impression of a qualified submission).
const FAULT_LEVEL_TO_AT_FAULT = {
  not_at_fault:       false,
  partially_at_fault: true,
  mostly_at_fault:    true,
  fully_at_fault:     true,
  // not_sure -> intentionally omitted (returns undefined -> null)
};

export default function FocusedFunnelForm(props) {
  return (
    <TrustedFormOffer enabled={trustedFormOn(props)}>
      <FocusedFunnelBody {...props} />
    </TrustedFormOffer>
  );
}

function FocusedFunnelBody({
  vertical,
  page,
  preview = false,
  demo = null,
  focusedFunnelConfig = {},
  session,
  experimentId = null,
  variant = "focused_funnel",
  onFocusedModeChange,
}) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const previewSurface = preview || Boolean(demo);
  const isSample = Boolean(vertical?.isSamplePage);
  const consentText = siteText((vertical?.tcpaLanguage && vertical.tcpaLanguage.trim()) ? vertical.tcpaLanguage : TCPA_LANGUAGE);
  const cfg = useMemo(() => resolveFocusedFunnelConfig(focusedFunnelConfig),
                      [focusedFunnelConfig]);

  const enabledFault = FAULT_OPTIONS.filter(
    (o) => cfg.fault_options_enabled[o.value] !== false);
  const enabledTimeframe = TIMEFRAME_OPTIONS.filter(
    (o) => cfg.timeframe_options_enabled[o.value] !== false);

  const { req, explicit } = useFieldRules({ style: "focused_funnel", page: fieldRulePage(vertical), buyer: vertical?.buyerCode });
  const forced = (k) => explicit(k) && req(k);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({
    accident_type: "",
    physically_injured: null,
    medical_treatment: null,
    currently_represented: null,
    fault_level: "",
    incident_timeframe: "",
    incident_date_exact: "",
    // Iter-63.1.12A — canonical MVA structured field. Rendered
    // between the attorney and fault steps when the selected
    // accident_type is an MVA case (car / truck / motorcycle /
    // rideshare / pedestrian / bicycle). "Other" skips this step.
    at_fault_party_insured: null,
    first_name: "", last_name: "", phone: "", email: "",
    state: "", zip: "",
    consent: false,
  });

  // Global funnels: report answers so a neutral page can name the matched firm.
  useEffect(() => {
    vertical?.onAnswers?.({
      ...answers,
      case_type: FF_ACCIDENT_TYPE_TO_CANONICAL[answers.accident_type]
        || answers.accident_type || null,
      incident_age_bucket: answers.incident_timeframe || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.state, answers.accident_type, answers.zip, vertical?.onAnswers]);

  // Focused-funnel step ladder — Iter-57.1 + Iter-58: the injury /
  // treatment / attorney qualifier questions appear by default because
  // their answers are valuable to the buyer regardless of the
  // disqualification rule. Admins can hide any of the three
  // independently via `show_*_question` flags (Iter-58). Only truly-
  // optional steps (like the exact-date follow-up) branch on config.
  // Hidden questions skip telemetry AND leave the corresponding lead
  // field null — we never fabricate an answer.
  const STEPS = useMemo(() => {
    const s = ["accident_type"];
    // Iter-63.1.12A verification -- resolve the consumer's raw
    // accident_type selection to its canonical backend value BEFORE
    // deciding MVA applicability. `car → auto`, everything else
    // passes through unchanged.
    const canonicalCaseType = FF_ACCIDENT_TYPE_TO_CANONICAL[answers.accident_type]
      || answers.accident_type;
    const isMvaSelection = requiresAtFaultPartyInsured({ caseType: canonicalCaseType });
    // Iter-63.1.12A verification -- canonical MVA required questions
    // (`physically_injured` / `medical_treatment` / `currently_represented`)
    // cannot be hidden by legacy admin config on an MVA lander because
    // the completeness engine unconditionally requires them and the
    // consumer would otherwise reach the contact step, submit, and
    // silently land as INCOMPLETE. When the visitor picks an MVA
    // accident_type we FORCE-render these three questions regardless
    // of the stored `show_*_question` flags. For non-MVA (`other`)
    // the admin's stored preference is respected. No answer is ever
    // fabricated -- if the step is hidden AND the case is non-MVA
    // the corresponding lead field stays null.
    const showInjury    = cfg.show_injury_question    || isMvaSelection || forced("physically_injured");
    const showTreatment = cfg.show_treatment_question || isMvaSelection || forced("medical_treatment");
    const showAttorney  = cfg.show_attorney_question  || isMvaSelection || forced("currently_represented");
    if (showInjury) s.push("injury");
    if (showTreatment) s.push("treatment");
    if (showAttorney) s.push("attorney");
    s.push("fault", "timeframe");
    // Iter-63.1.12A — insert the AFPI step ONLY when the visitor's
    // (canonicalised) accident_type is an MVA case. `answers.accident_type`
    // is set on Q1 (auto-advance) before we reach any downstream
    // step, so the step ladder is stable by the time the visitor
    // walks past it. If the accident_type is "other" (non-MVA) the
    // AFPI step is not present at all.
    if (isMvaSelection) {
      s.push("at_fault_party_insured");
    }
    if (cfg.require_exact_date_after_qualification) {
      // Iter-63.1.2: legacy configs may still carry this flag. Ignore it
      // — the exact-date step is no longer part of the funnel; the
      // bucket dropdown alone is authoritative for buyer routing.
    }
    s.push("contact");
    return s;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.show_injury_question, cfg.show_treatment_question,
      cfg.show_attorney_question,
      cfg.require_exact_date_after_qualification,
      answers.accident_type, req, explicit]);

  const currentKey = STEPS[i];
  const [disqualified, setDisqualified] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [previewSubmitted, setPreviewSubmitted] = useState(false);
  const turnstileEnabled = Boolean(TURNSTILE_SITE_KEY) && !previewSurface && !isSample;

  // Stable per-session id for funnel telemetry. Only ever hits the
  // backend when we're on a real production surface (never preview /
  // demo / sample).
  const sessionIdRef = useRef(null);
  const turnstileRef = useRef(null);
  if (sessionIdRef.current === null) {
    sessionIdRef.current = session?.sessionId
      || `ff_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  }
  const funnelSessionId = sessionIdRef.current;

  const isRealSurface = !previewSurface && !isSample;
  const affRef = params.get("aff") || "";
  const subId = params.get("sub") || params.get("sub_id") || "";
  const utmSource = params.get("utm_source") || null;
  const utmMedium = params.get("utm_medium") || null;
  const utmCampaign = params.get("utm_campaign") || null;

  const emitTelemetry = useCallback((endpoint, body) => {
    if (!isRealSurface) return;   // NEVER on preview/demo/sample
    // Fire-and-forget — never block UX on telemetry writes.
    api.post(endpoint, {
      session_id: funnelSessionId,
      slug: vertical?.slug,
      preview: false,
      ...body,
    }).catch(() => { /* swallow — analytics failures are non-blocking */ });
  }, [isRealSurface, funnelSessionId, vertical?.slug]);

  // Fire form_start once when the visitor selects an accident type — the
  // same signal Standard/Quiz emit at the top of their form. Iter-56.3
  // exact-trigger matching still applies.
  const formStartFiredRef = useRef(false);
  const emitFormStartTracking = useCallback(() => {
    if (formStartFiredRef.current) return;
    formStartFiredRef.current = true;
    if (isRealSurface) {
      try {
        window.dispatchEvent(new CustomEvent("lf-tracking-emit", {
          detail: { event: "form_start", context: {} },
        }));
      } catch (_e) { /* noop */ }
    }
    // Iter-63.1: prospect-share `form_start` on demo surfaces only.
    if (demo && vertical?.customSlug && !isSample) {
      emitPreviewEngagement(demo, vertical.customSlug, "form_start", {
        isSamplePage: isSample,
      });
    }
  }, [isRealSurface, demo, vertical?.customSlug, isSample]);

  // Start the funnel session as soon as the component mounts on a real
  // surface. Never fires on preview / demo / sample.
  useEffect(() => {
    emitTelemetry("/public/focused-funnel/session/start", {
      variant, experiment_id: experimentId, sub_id: subId,
      utm_source: utmSource, utm_medium: utmMedium, utm_campaign: utmCampaign,
    });
    emitTelemetry("/public/focused-funnel/session/event", {
      event_type: "landing_page_view", step_key: null,
    });
    // We intentionally only run on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever we move past Q1, tell the parent to hide marketing
  // sections. Undo it if the user hits Back to Q1.
  useEffect(() => {
    if (onFocusedModeChange) {
      onFocusedModeChange(i > 0);
    }
  }, [i, onFocusedModeChange]);

  const goNext = () => setI((n) => Math.min(n + 1, STEPS.length - 1));
  const goBack = () => setI((n) => Math.max(n - 1, 0));

  const percent = Math.round(((i + 1) / STEPS.length) * 100);

  const selectAccidentType = (value) => {
    setAnswers((a) => ({ ...a, accident_type: value }));
    emitTelemetry("/public/focused-funnel/session/event", {
      event_type: "accident_type_selected", step_key: "accident_type",
      accident_type: value,
    });
    emitFormStartTracking();
    goNext();
  };

  const disqualify = (reason, stepKey) => {
    setDisqualified(reason);
    emitTelemetry("/public/focused-funnel/session/disqualify", {
      step_key: stepKey, reason,
      accident_type: answers.accident_type || null,
      incident_timeframe: answers.incident_timeframe || null,
    });
  };

  const answerYesNo = (fieldKey, stepKey, value) => {
    setAnswers((a) => ({ ...a, [fieldKey]: value }));
    emitTelemetry("/public/focused-funnel/session/event", {
      event_type: "question_answered", step_key: stepKey,
    });
    // Qualification gates. Every gate is admin-toggleable — this only
    // fires when the corresponding `require_*` is TRUE.
    if (stepKey === "injury" && cfg.require_injury && value === false) {
      return disqualify("no_injury", "injury");
    }
    if (stepKey === "treatment" && cfg.require_treatment && value === false) {
      return disqualify("no_treatment", "treatment");
    }
    if (stepKey === "attorney" && cfg.require_no_attorney && value === true) {
      return disqualify("has_attorney", "attorney");
    }
    goNext();
  };

  const selectFault = (val) => {
    setAnswers((a) => ({ ...a, fault_level: val }));
    emitTelemetry("/public/focused-funnel/session/event", {
      event_type: "question_answered", step_key: "fault", fault_level: val,
    });
    goNext();
  };
  const selectTimeframe = (val) => {
    setAnswers((a) => ({ ...a, incident_timeframe: val }));
    emitTelemetry("/public/focused-funnel/session/event", {
      event_type: "question_answered", step_key: "timeframe",
      incident_timeframe: val,
    });
    goNext();
  };

  // Fire the contact-reached beacon exactly once when the visitor lands
  // on the contact step.
  const contactBeaconRef = useRef(false);
  useEffect(() => {
    if (currentKey === "contact" && !contactBeaconRef.current) {
      contactBeaconRef.current = true;
      emitTelemetry("/public/focused-funnel/session/event", {
        event_type: "contact_reached", step_key: "contact",
      });
    }
  }, [currentKey, emitTelemetry]);

  const contactValid = () => {
    const requireConsent = cfg.consent_presentation === "affirmative_checkbox";
    return Boolean(
      answers.first_name.trim() && answers.last_name.trim()
      // Iter-62.3.2: strict shared phone validator.
      && isValidUSPhone(answers.phone) && answers.state
      // Iter-74.2 — Email + ZIP REQUIRED on public forms.
      && answers.email.trim() && isValidEmail(answers.email)
      && zipOk(answers.zip, req("zip"), isValidZip)
      && (!requireConsent || answers.consent === true),
    );
  };

  const selectAfpi = (val) => {
    setAnswers((a) => ({ ...a, at_fault_party_insured: val }));
    emitTelemetry("/public/focused-funnel/session/event", {
      event_type: "question_answered", step_key: "at_fault_party_insured",
    });
    goNext();
  };

  const submit = async () => {
    setError("");
    if (!contactValid()) {
      setError("Please complete every required field before submitting.");
      return;
    }
    emitTelemetry("/public/focused-funnel/session/event", {
      event_type: "submit_click", step_key: "contact",
    });

    // Preview + demo + sample → route directly to Thank-You with no
    // lead insertion, no TrustedForm read, no conversion pixel.
    if (previewSurface) {
      if (demo && vertical?.customSlug) {
        const q = new URLSearchParams({
          slug: vertical.customSlug, demo,
          ...(vertical.firmName ? { firm: vertical.firmName } : {}),
          ...(vertical.firmPhone ? { phone: vertical.firmPhone } : {}),
          ...(vertical.funnelSlug ? { funnel: vertical.funnelSlug } : {}),
          ...(vertical.buyerCode ? { b: vertical.buyerCode } : {}),
        });
        nav(`/lp/thank-you?${q.toString()}`);
        return;
      }
      setPreviewSubmitted(true);
      return;
    }

    setSubmitting(true);
    const trustedform_url = isSample ? null : readTrustedFormCert();
    // Iter-63.1.12A verification -- canonical accident_type resolution.
    // The consumer's raw funnel selection (car / truck / motorcycle /
    // rideshare / pedestrian / bicycle / other) is normalised via
    // FF_ACCIDENT_TYPE_TO_CANONICAL and sent through the existing
    // `case_type` contract. The backend refines `accident_type` from
    // `case_type` (public_routes._CASE_TYPE_TO_ACCIDENT_TYPE) so the
    // final stored canonical accident_type ALWAYS reflects the
    // consumer's actual selection -- independent of whichever landing
    // page slug hosts the funnel.
    const canonicalCaseType = FF_ACCIDENT_TYPE_TO_CANONICAL[answers.accident_type]
      || answers.accident_type
      || "other";
    const legacyLanding = page?.slug || vertical?.slug || canonicalCaseType;
    // Iter-63.1.12A verification -- truthful `at_fault` derivation.
    // `not_at_fault` -> false; all other explicit fault levels -> true;
    // `not_sure` -> null (leaves the lead INCOMPLETE per the canonical
    // completeness engine, never fabricated).
    const derivedAtFault = Object.prototype.hasOwnProperty.call(
      FAULT_LEVEL_TO_AT_FAULT, answers.fault_level,
    ) ? FAULT_LEVEL_TO_AT_FAULT[answers.fault_level] : null;

    const payload = {
      aff: affRef || null,
      sub_id: subId || null,
      landing_page: legacyLanding,
      // Iter-63.1.12A verification -- always send canonical `case_type`
      // so the backend maps to the correct final accident_type for
      // every funnel selection (not just "other"). The verbatim raw
      // selection stays on lead.extra for reporting parity.
      case_type: canonicalCaseType,
      first_name: answers.first_name.trim(),
      last_name: answers.last_name.trim(),
      phone: answers.phone.trim(),
      email: answers.email.trim() || null,
      state: answers.state,
      zip: answers.zip.trim() || null,
      // Iter-57 intentionally does NOT fabricate at_fault or incident_date
      // from the funnel's bucket answers. Those remain independent lead
      // fields; the funnel extras live on lead.extra.
      incident_date: cfg.require_exact_date_after_qualification
        ? (answers.incident_date_exact || null)
        : null,
      // Iter-63.1.12A verification -- TRUTHFUL derivation from
      // fault_level. See FAULT_LEVEL_TO_AT_FAULT above.
      at_fault: derivedAtFault,
      currently_represented: answers.currently_represented,
      physically_injured: answers.physically_injured,
      medical_treatment: answers.medical_treatment,
      // Iter-63.1.12A — canonical MVA structured field. Null when the
      // visitor was routed to a non-MVA accident_type (AFPI step is
      // omitted from the ladder in that case) so we never fabricate.
      at_fault_party_insured: answers.at_fault_party_insured || null,
      accident_description: null,
      police_report_number: null,
      accident_photo_ids: [],
      consent: cfg.consent_presentation === "affirmative_checkbox"
        ? answers.consent === true
        : true,  // disclosure-below-form mode: submission itself constitutes acceptance of the disclosed language
      tcpa_language: (vertical?.tcpaLanguage && vertical.tcpaLanguage.trim())
        ? vertical.tcpaLanguage : TCPA_LANGUAGE,
      trustedform_url,
      landing_url: window.location.href,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      turnstile_token: turnstileToken || null,
      company: null,
      load_ms: session ? session.elapsedMs() : null,
      form_experience: "focused_funnel",
      experiment_id: experimentId,
      experiment_variant: variant || "focused_funnel",
      quiz_session_id: null,
      focused_funnel_session_id: funnelSessionId,
      fault_level: answers.fault_level || null,
      incident_timeframe: answers.incident_timeframe || null,
      // Iter-62.3.2: Focused Funnel now uses the CANONICAL five-window
      // vocabulary — same values Quiz sends. Dual-write to
      // `incident_age_bucket` so `buyer_eligibility._bucket_age_range`
      // routes on the canonical Quiz map (`QUIZ_BUCKET_DAYS`). Legacy
      // `incident_timeframe` is preserved for backward-compat
      // analytics — see the compatibility mapping in
      // `buyer_eligibility.FF_TIMEFRAME_DAYS` / focusedFunnelConfig.js.
      incident_age_bucket: answers.incident_timeframe || null,
      buyer_code: vertical?.buyerCode || null,
      buyer_source: vertical?.buyerSource || null,
    };
    try {
      const { data } = await api.post("/public/landing/lead", payload);
      const q = new URLSearchParams({
        lead_id: data.id,
        vertical: legacyLanding,
        ...(data.is_test ? { test: "1" } : {}),
        ...routedThankYou(vertical, data),
        ...(vertical?.funnelSlug ? { funnel: vertical.funnelSlug } : {}),
        experience: "focused_funnel",
      });
      nav(`/lp/thank-you?${q.toString()}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || "Something went wrong — please try again.";
      setError(typeof msg === "string" ? msg
                : "Submission failed. Please try again.");
      // A Turnstile token works once: get a fresh one for the retry.
      setTurnstileToken("");
      turnstileRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  if (previewSubmitted) {
    return (
      <div className="ff-card" data-testid="focused-funnel-preview-submitted">
        <div className="ff-preview-success">
          <strong>PREVIEW SUBMITTED</strong>
          <p>This is a preview — no real lead was sent.</p>
        </div>
      </div>
    );
  }

  if (disqualified) {
    const copy = DISQUALIFICATION_COPY[disqualified] || DISQUALIFICATION_COPY.no_injury;
    return (
      <div className="ff-card" data-testid={`focused-funnel-disqualified-${disqualified}`}>
        <div className="ff-disqualified">
          <div className="ff-badge">◆</div>
          <h2 data-testid="focused-funnel-dq-headline">{copy.headline}</h2>
          <p data-testid="focused-funnel-dq-message">{copy.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ff-card" data-testid={`focused-funnel-step-${currentKey}`}>
      {previewSurface && !formBadgeHidden() && (
        <div className="ff-preview-banner" data-testid="focused-funnel-preview-banner">
          PREVIEW · No submission will be sent
        </div>
      )}

      {/* Progress — hidden on Q1 so the lander stays clean */}
      {i > 0 && (
        <div className="ff-progress" data-testid="focused-funnel-progress">
          <div className="ff-progress-bar">
            <div className="ff-progress-fill" style={{ width: `${percent}%` }} />
          </div>
          <div className="ff-progress-label">
            Question {i + 1} of {STEPS.length}
          </div>
        </div>
      )}

      {/* Back — hidden on Q1 */}
      {i > 0 && (
        <button
          type="button"
          onClick={goBack}
          className="ff-back"
          data-testid="focused-funnel-back"
        >
          <ArrowLeft size={14} /> Back
        </button>
      )}

      {currentKey === "accident_type" && (
        <div className="ff-step" data-testid="focused-funnel-step-accident-type">
          <h2 className="ff-question">What type of accident were you involved in?</h2>
          <div className="ff-icon-grid" role="radiogroup" aria-label="Accident type">
            {ACCIDENT_TYPE_OPTIONS.map((opt) => {
              const Icon = ICON_MAP[opt.icon] || HelpCircle;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={answers.accident_type === opt.value}
                  onClick={() => selectAccidentType(opt.value)}
                  data-testid={`focused-funnel-accident-${opt.value}`}
                  className="ff-icon-card"
                >
                  <span className="ff-icon-wrap"><Icon size={36} strokeWidth={1.5} /></span>
                  <span className="ff-icon-label">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {currentKey === "injury" && (
        <YesNoStep
          headline="Were you physically injured in the accident?"
          testId="focused-funnel-step-injury"
          onAnswer={(v) => answerYesNo("physically_injured", "injury", v)}
          yesFirst
        />
      )}

      {currentKey === "treatment" && (
        <YesNoStep
          headline="Have you received medical treatment for your injuries?"
          testId="focused-funnel-step-treatment"
          onAnswer={(v) => answerYesNo("medical_treatment", "treatment", v)}
          yesFirst
        />
      )}

      {currentKey === "attorney" && (
        <YesNoStep
          headline="Do you currently have an attorney representing you for this accident?"
          testId="focused-funnel-step-attorney"
          onAnswer={(v) => answerYesNo("currently_represented", "attorney", v)}
          yesFirst={false}
        />
      )}

      {currentKey === "fault" && (
        <div className="ff-step" data-testid="focused-funnel-step-fault">
          <h2 className="ff-question">How would you describe fault in the accident?</h2>
          <div className="ff-choice-list">
            {enabledFault.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => selectFault(opt.value)}
                data-testid={`focused-funnel-fault-${opt.value}`}
                className="ff-choice-card"
              >{opt.label}</button>
            ))}
          </div>
        </div>
      )}

      {currentKey === "timeframe" && (
        <div className="ff-step" data-testid="focused-funnel-step-timeframe">
          <h2 className="ff-question">When did the accident happen?</h2>
          {/* Iter-63.1.2: dropdown replaces the choice-card list.
              Auto-advance preserved on non-empty selection. */}
          <select
            className="ff-input"
            value={answers.incident_timeframe || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v) selectTimeframe(v);
            }}
            data-testid="focused-funnel-timeframe"
          >
            <option value="">Select when the accident happened…</option>
            {enabledTimeframe.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
                data-testid={`focused-funnel-timeframe-${opt.value}`}
              >{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Iter-63.1.12A — canonical MVA structured question. Present
          only when the selected accident_type is an MVA case (STEPS
          is filtered above). */}
      {currentKey === "at_fault_party_insured" && (
        <div className="ff-step" data-testid="focused-funnel-step-at-fault-party-insured">
          <h2 className="ff-question">
            Was the other driver or at-fault party insured?
          </h2>
          <div className="ff-choice-list">
            {AFPI_CHOICES.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => selectAfpi(opt.value)}
                data-testid={`focused-funnel-afpi-${opt.value}`}
                className="ff-choice-card"
              >{opt.label}</button>
            ))}
          </div>
        </div>
      )}

      {FF_RULE_KEY[currentKey] && !req(FF_RULE_KEY[currentKey]) && (
        <button type="button" onClick={goNext} className="ff-back" data-testid="focused-funnel-skip">
          Skip this question
        </button>
      )}

      {currentKey === "contact" && (
        <div className="ff-step" data-testid="focused-funnel-step-contact">
          <h2 className="ff-question">
            {cfg.contact_headline || "Almost done — where can we reach you?"}
          </h2>
          <div className="ff-contact-grid">
            <input placeholder="First name" className="ff-input" value={answers.first_name}
              onChange={(e) => setAnswers((a) => ({ ...a, first_name: e.target.value }))}
              data-testid="focused-funnel-first-name" name="first_name" autoComplete="given-name"/>
            <input placeholder="Last name" className="ff-input" value={answers.last_name}
              onChange={(e) => setAnswers((a) => ({ ...a, last_name: e.target.value }))}
              data-testid="focused-funnel-last-name" name="last_name" autoComplete="family-name"/>
            <input placeholder="585-545-8888" className="ff-input" value={answers.phone} type="tel"
              inputMode="tel"
              onChange={(e) => setAnswers((a) => ({ ...a, phone: formatUSPhone(e.target.value) }))}
              data-testid="focused-funnel-phone" name="phone" autoComplete="tel"/>
            <input placeholder="Email *" className="ff-input" required value={answers.email} type="email"
              onChange={(e) => setAnswers((a) => ({ ...a, email: e.target.value }))}
              data-testid="focused-funnel-email" name="email" autoComplete="email"/>
            <select className="ff-input" value={answers.state}
              onChange={(e) => setAnswers((a) => ({ ...a, state: e.target.value }))}
              data-testid="focused-funnel-state" name="state" autoComplete="address-level1">
              <option value="">State</option>
              {US_STATES.map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
            <input placeholder={req("zip") ? "ZIP *" : "ZIP (optional)"} className="ff-input" required={req("zip")} value={answers.zip}
              onChange={(e) => setAnswers((a) => ({ ...a, zip: e.target.value }))}
              data-testid="focused-funnel-zip" name="zip" autoComplete="postal-code"/>
          </div>

          {/* Honeypot: hidden — real users can't fill it. */}
          <input
            type="text" name="company" tabIndex={-1} autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", height: 0, width: 0 }}
          />

          {/* TCPA — checkbox or disclosure-below-form depending on config. */}
          {cfg.consent_presentation === "affirmative_checkbox" ? (
            <label className="ff-consent" data-tf-element-role="consent-language" data-testid="focused-funnel-consent-checkbox">
              <input
                type="checkbox" name="consent" checked={answers.consent === true}
                onChange={(e) => setAnswers((a) => ({ ...a, consent: e.target.checked }))}
                data-tf-element-role="consent-opt-in"
                data-testid="focused-funnel-consent-input"
              />
              <span className="ff-consent-text">
                <ConsentText text={consentText} advertiser={vertical?.firmName} />
              </span>
            </label>
          ) : (
            <div className="ff-disclosure" data-tf-element-role="consent-language" data-testid="focused-funnel-consent-disclosure">
              <ConsentText text={consentText} advertiser={vertical?.firmName} />
            </div>
          )}

          {turnstileEnabled && (
            <div className="ff-turnstile">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={setTurnstileToken}
                onError={() => setTurnstileToken("")}
                onExpire={() => setTurnstileToken("")}
                options={{ theme: "light", size: "flexible" }}
              />
            </div>
          )}

          {error && (
            <div className="ff-error" data-testid="focused-funnel-error" role="alert">
              {error}
            </div>
          )}

          <button
            type="button"
            className="ff-primary"
            onClick={submit}
            disabled={submitting || (turnstileEnabled && !turnstileToken)}
            name="submit"
            data-tf-element-role="submit"
            data-testid="focused-funnel-submit"
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </div>
      )}
    </div>
  );
}

function YesNoStep({ headline, onAnswer, testId, yesFirst = true }) {
  return (
    <div className="ff-step" data-testid={testId}>
      <h2 className="ff-question">{headline}</h2>
      <div className="ff-yesno" role="radiogroup">
        {[yesFirst ? true : false, yesFirst ? false : true].map((v) => (
          <button
            key={String(v)}
            type="button"
            role="radio"
            onClick={() => onAnswer(v)}
            aria-checked={false}
            data-testid={`${testId}-${v ? "yes" : "no"}`}
            className="ff-yesno-card"
          >{v ? "YES" : "NO"}</button>
        ))}
      </div>
    </div>
  );
}
